import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import './PromptStats.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const VARIABLE_PATTERN = /(?<!\{)\{([a-zA-Z_][a-zA-Z0-9_]*)\}(?!\})/g;

function PromptStats({ text, knownVariables = [], label, estimateEndpoint, entityId }) {
  const [apiStats, setApiStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Local detection (instant, always available)
  const localStats = useMemo(() => {
    if (!text) return null;
    const matches = new Set();
    let match;
    const regex = new RegExp(VARIABLE_PATTERN.source, 'g');
    while ((match = regex.exec(text)) !== null) {
      matches.add(match[1]);
    }
    return {
      detected: Array.from(matches).sort(),
      estimatedTokens: Math.ceil(text.length / 4),
    };
  }, [text]);

  // API-based estimation (debounced)
  useEffect(() => {
    if (!text || !estimateEndpoint) {
      setApiStats(null);
      return;
    }

    setLoading(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const body = { prompt: text };
        if (entityId !== undefined && entityId !== null) {
          // Layer 1 uses doc_id, Layer 2 uses guideline_id
          if (estimateEndpoint.includes('llm-generated-guidelines')) {
            body.guideline_id = entityId;
          } else {
            body.doc_id = entityId;
          }
        }
        const response = await axios.post(`${API_BASE_URL}${estimateEndpoint}`, body);
        setApiStats(response.data);
      } catch (err) {
        console.error('Error estimating prompt tokens:', err);
        setApiStats(null);
      } finally {
        setLoading(false);
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [text, estimateEndpoint, entityId]);

  if (!localStats) return null;

  // Use API stats when available, local as fallback
  const detected = apiStats ? apiStats.detected_variables : localStats.detected;
  const tokens = apiStats ? apiStats.estimated_tokens : localStats.estimatedTokens;
  const isEstimate = !apiStats;

  return (
    <div className="prompt-stats">
      {label && <span className="prompt-stats-label">{label}:</span>}
      <span className="prompt-stats-tokens">
        {loading ? '...' : `~${tokens.toLocaleString()} tokens`}
        {isEstimate && !loading && estimateEndpoint && (
          <span className="prompt-stats-estimate-note">(template only)</span>
        )}
      </span>
      {detected.length > 0 && (
        <span className="prompt-stats-vars">
          {detected.map((v) => (
            <span
              key={v}
              className={`prompt-stats-pill ${knownVariables.includes(v) ? 'known' : 'unknown'}`}
            >
              {`{${v}}`}
            </span>
          ))}
        </span>
      )}
      {detected.length === 0 && (
        <span className="prompt-stats-no-vars">No variables detected</span>
      )}
    </div>
  );
}

export default PromptStats;
